import React, { useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Database,
  DownloadSimple,
  UploadSimple,
  Warning,
  CheckCircle,
  CloudArrowDown,
  CloudArrowUp,
  Info,
} from "@phosphor-icons/react";

export default function DataTools() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exportStats, setExportStats] = useState(null);
  const fileRef = useRef(null);

  const onExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/export-all");
      const data = r.data;
      const counts = Object.fromEntries(
        Object.entries(data.collections || {}).map(([k, v]) => [k, v.length])
      );
      setExportStats({ exported_at: data.exported_at, counts });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `delhigence-snapshot-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Snapshot downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm("This will WIPE all current data in this environment and replace it with the snapshot. Continue?")) {
      e.target.value = "";
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const snapshot = JSON.parse(text);
      const r = await api.post("/admin/import-all", { snapshot, wipe: true });
      setImportResult(r.data);
      toast.success("Snapshot restored. You may need to sign in again with the imported credentials.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const header = (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: "Geist" }}>
        <Database size={22} /> Data Tools
      </h1>
      <p className="text-sm text-slate-500 mt-0.5">
        Export and restore complete environment snapshots — used to migrate between Preview and Production.
      </p>
    </div>
  );

  return (
    <AppLayout header={header}>
      <div className="max-w-3xl space-y-6" data-testid="data-tools-page">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CloudArrowDown size={20} className="text-[#0B2A5B]" /> Export Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Downloads a JSON file containing every user, company, audit cycle, observation,
              resolution, comment, attachment record and activity in the current environment.
              Password hashes are included so users keep their existing credentials.
            </p>
            <Button onClick={onExport} disabled={exporting} className="bg-[#0B2A5B] hover:bg-[#0B2A5B]/90" data-testid="export-snapshot-btn">
              <DownloadSimple size={16} className="mr-1" />
              {exporting ? "Preparing..." : "Download Snapshot"}
            </Button>
            {exportStats && (
              <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded-md p-3 text-xs text-emerald-900" data-testid="export-stats">
                <div className="font-semibold flex items-center gap-1.5"><CheckCircle size={13} weight="fill" /> Exported at {new Date(exportStats.exported_at).toLocaleString()}</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-2">
                  {Object.entries(exportStats.counts).map(([k, v]) => (
                    <div key={k} className="bg-white border border-emerald-200 rounded px-2 py-1">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700">{k}</div>
                      <div className="font-semibold text-emerald-900">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CloudArrowUp size={20} className="text-amber-700" /> Import Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
              <Warning size={14} className="mt-0.5 shrink-0" weight="fill" />
              <div>
                <span className="font-semibold">This will wipe all existing data</span> in this environment
                (users, companies, cycles, observations, resolutions, comments, attachments, activities)
                and replace it with the contents of the snapshot file. You will likely need to sign in again
                with the credentials from the source environment after import.
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={onImportFile}
              className="hidden"
              data-testid="import-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={importing}
              variant="outline"
              className="border-amber-400 text-amber-800 hover:bg-amber-50"
              data-testid="import-snapshot-btn"
            >
              <UploadSimple size={16} className="mr-1" />
              {importing ? "Restoring..." : "Choose snapshot JSON to restore"}
            </Button>
            {importResult && (
              <div className="mt-3 border border-emerald-200 bg-emerald-50 rounded-md p-3 text-xs text-emerald-900" data-testid="import-result">
                <div className="font-semibold flex items-center gap-1.5"><CheckCircle size={13} weight="fill" /> Restore complete</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-2">
                  {Object.entries(importResult.restored || {}).map(([k, v]) => (
                    <div key={k} className="bg-white border border-emerald-200 rounded px-2 py-1">
                      <div className="text-[10px] uppercase tracking-wider text-emerald-700">{k}</div>
                      <div className="font-semibold text-emerald-900">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Info size={16} /> Migration playbook
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-2 leading-relaxed">
            <ol className="list-decimal pl-5 space-y-1">
              <li>On <strong>Preview</strong>, log in as the audit admin and click <em>Download Snapshot</em>.</li>
              <li>Deploy your app to <strong>Production</strong> and open <code className="bg-slate-200 px-1 rounded text-xs">auditai.delhigence.com</code>.</li>
              <li>You'll see the <em>First-time setup</em> form. Create a temporary admin (any email/password).</li>
              <li>Open <em>Data Tools</em> in the sidebar and use <em>Import Snapshot</em>.</li>
              <li>After import completes, sign out — your real preview credentials now work in production.</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
